import React from 'react';
import {
    Modal,
    Box,
    Typography,
    Button,
    List,
    ListItem,
    ListItemText,
    Paper,
    IconButton,
    Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CelebrationIcon from '@mui/icons-material/Celebration';

const translations = {
    en: {
        title: "🎉 Premium Features Unlocked!",
        subtitle: "Free access until August 10th, 2025",
        description: "We're excited to give you temporary access to all Premium features at no cost!",
        features: [
            "🔄 Unlimited Quick book and control your booking", 
            "⭐ Save unlimited courts as favorites",
            "🗺️ Advanced Map view (Custom markers)",
            "🔮 Predictive booking links",
            "⚡ Can enable automatic refresh"
        ],
        cta: "Start Exploring Premium Features",
        close: "Close",
        enjoyMessage: "Enjoy exploring all the premium features!"
    },
    fr: {
        title: "🎉 Fonctionnalités Premium débloquées !",
        subtitle: "Accès gratuit jusqu'au 10 août 2025",
        description: "Nous sommes ravis de vous donner un accès temporaire à toutes les fonctionnalités Premium gratuitement !",
        features: [
            "🔄 Réservation directe illimitée et contrôle de vos réservations",
            "⭐ Sauvegarde illimitée des terrains favoris", 
            "🗺️ Vue carte avancée (marqueurs personnalisés)",
            "🔮 Liens de réservation prédictifs",
            "⚡ Possibilité d'activer le rafraîchissement automatique"
        ],
        cta: "Commencer à explorer les fonctionnalités Premium",
        close: "Fermer",
        enjoyMessage: "Profitez de toutes les fonctionnalités premium !"
    }
};

const StyledModal = styled(Modal)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
});

const ModalContent = styled(Paper)(({ theme }) => ({
    maxWidth: '500px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    borderRadius: '16px',
    position: 'relative',
    background: 'var(--cc-surface)',
    color: 'var(--cc-text)',
    boxShadow: 'var(--cc-shadow-raised)',
    [theme.breakpoints.down('sm')]: {
        margin: '10px',
        maxWidth: 'calc(100vw - 20px)'
    }
}));

const ContentBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(3)
    }
}));

const FeaturesList = styled(List)(({ theme }) => ({
    backgroundColor: 'var(--cc-premium-soft)',
    border: '1px solid var(--cc-border)',
    borderRadius: '12px',
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
    backdropFilter: 'blur(10px)'
}));

const FeatureItem = styled(ListItem)(({ theme }) => ({
    padding: theme.spacing(1, 0),
    '&:not(:last-child)': {
        borderBottom: '1px solid var(--cc-border)'
    }
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    color: 'var(--cc-text-soft)',
    '&:hover': {
        color: 'var(--cc-text)',
        backgroundColor: 'var(--cc-hover)'
    }
}));

const CTAButton = styled(Button)(({ theme }) => ({
    background: 'var(--cc-premium)',
    border: 0,
    borderRadius: '25px',
    boxShadow: 'var(--cc-shadow-soft)',
    color: 'var(--cc-on-accent)',
    height: 48,
    padding: '0 30px',
    fontSize: '1rem',
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': {
        background: 'var(--cc-premium)',
        boxShadow: 'var(--cc-shadow-raised)',
        transform: 'translateY(-2px)'
    },
    transition: 'all 0.3s ease'
}));

export default function PremiumAnnouncementModal({ open, onClose, isFrench = false }) {
    const t = isFrench ? translations.fr : translations.en;

    return (
        <StyledModal
            open={open}
            onClose={onClose}
            aria-labelledby="premium-announcement-title"
            aria-describedby="premium-announcement-description"
        >
            <ModalContent>
                <CloseButton onClick={onClose} aria-label="close">
                    <CloseIcon />
                </CloseButton>
                
                <ContentBox>
                    {/* Header */}
                    <Box textAlign="center" mb={3}>
                        <CelebrationIcon sx={{ fontSize: 48, mb: 2, color: 'var(--cc-star)' }} />
                        <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
                            {t.title}
                        </Typography>
                        <Chip
                            label={t.subtitle}
                            sx={{
                                backgroundColor: 'var(--cc-premium-soft)',
                                color: 'var(--cc-premium)',
                                fontWeight: 600,
                                fontSize: '0.9rem',
                                backdropFilter: 'blur(10px)'
                            }}
                        />
                    </Box>

                    {/* Description */}
                    <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', fontSize: '1.1rem', lineHeight: 1.6 }}>
                        {t.description}
                    </Typography>

                    {/* Features List */}
                    <FeaturesList>
                        {t.features.map((feature, index) => (
                            <FeatureItem key={index}>
                                <ListItemText
                                    primary={feature}
                                    primaryTypographyProps={{
                                        sx: { fontSize: '1rem', fontWeight: 500 }
                                    }}
                                />
                            </FeatureItem>
                        ))}
                    </FeaturesList>

                    {/* CTA Button */}
                    <Box textAlign="center">
                        <CTAButton
                            onClick={onClose}
                            size="large"
                            fullWidth
                        >
                            {t.cta}
                        </CTAButton>
                        <Typography variant="body2" sx={{ mt: 2, opacity: 0.9, fontStyle: 'italic' }}>
                            {t.enjoyMessage}
                        </Typography>
                    </Box>
                </ContentBox>
            </ModalContent>
        </StyledModal>
    );
}
